import { IApplicationState, VSConfiguration } from '@bees-web/core/types'
import { createSelector } from '@reduxjs/toolkit'
import { useEffect, useMemo } from 'react'

import { AppState, configureContextualStore } from '@/core/store'
import { InteractivesBuilder, UseInteractive } from '@/core/types'

import { combineInteractives } from '../interactives'

type UseValueStreamResult = {
  createInteractiveMap: InteractivesBuilder
  stateSelector: (state: AppState) => IApplicationState
}

export const useValueStream = (
  workspace: VSConfiguration,
  dependencies?: VSConfiguration[]
): UseValueStreamResult => {
  const modules = useMemo(
    () => [...(dependencies || []), workspace],
    [dependencies, workspace]
  )

  const interactives = useMemo(() => {
    const map: Record<string, UseInteractive> = {}
    for (const mod of modules) {
      if (mod.redux) map[mod.namespace] = mod.redux.actions?.interactiveMap
    }
    return map
  }, [modules])

  const workspaces = useMemo(() => {
    const map: Record<string, VSConfiguration> = {}
    for (const mod of modules) {
      map[mod.namespace] = mod
    }
    return map
  }, [modules])

  useEffect(() => {
    configureContextualStore(workspaces)
  }, [workspaces])

  const stateSelector = useMemo(
    () =>
      createSelector([(state: AppState) => state], (state) => ({
        ...state,
        state: modules.length
          ? modules.reduce((acc, curr) => Object.assign(acc, state[curr.namespace]), {})
          : state,
      })),
    [modules]
  )

  const createInteractiveMap = useMemo(
    () => combineInteractives(interactives),
    [interactives]
  )

  return {
    createInteractiveMap,
    stateSelector,
  }
}
